/**
 * Sortable table header cell.
 *
 * A real `<button>` inside the `<th>` with `aria-sort`, so keyboard users can
 * sort and screen readers announce the current direction. Clicking an already
 * sorted column flips direction; clicking a different column starts at desc,
 * which is what operators expect for money and dates ("biggest/newest first").
 */

import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { TableHead } from '@/components/ui/table'
import type { TableSort } from '@limo/shared'

interface Props<F extends string> {
  readonly field: F
  readonly label: string
  readonly sort: TableSort<F>
  readonly onSort: (next: TableSort<F>) => void
  readonly className?: string
}

export function SortableHeader<F extends string>({
  field,
  label,
  sort,
  onSort,
  className,
}: Props<F>) {
  const isActive = sort.field === field
  const direction = isActive ? sort.direction : undefined

  const ariaSort: 'ascending' | 'descending' | 'none' = isActive
    ? sort.direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none'

  return (
    <TableHead className={className} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() =>
          onSort({ field, direction: isActive && sort.direction === 'desc' ? 'asc' : 'desc' })
        }
        className="inline-flex items-center gap-1 font-medium transition-colors hover:text-foreground"
      >
        <span>{label}</span>
        {direction === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : direction === 'desc' ? (
          <ArrowDown className="h-3 w-3" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </TableHead>
  )
}
